<!-- eslint-disable vue/no-parsing-error -->
<template>
  <template v-if="isAccountLoading">
    <Loading />
  </template>
  <template v-else>
    <div class="md:max-w-screen-xl min-h-screen relative">
      <div class="my-3" v-if="showBankVerifyAlert">
        <AlertToast>
          <div class="py-1 md:py-2 px-3 md:px-5 flex flex-col">
            <div class="flex flex-row py-2">
              <ExclamationIcon class="text-brand-yellow w-8 h-8 mr-2" />
              <h2>Verify Your Bank Account</h2>
            </div>
            <p class="py-2">
              You must verify your bank account to complete the setup of your My Payments account
              and be able to receive withdrawals.
            </p>
            <div class="flex flex-row justify-between md:justify-start py-3">
              <div class="w-32 mr-5">
                <SecondaryButton @click="closeBankVerifyAlert">Not now</SecondaryButton>
              </div>
              <router-link :to="{ name: 'dashboard_settings_bank_account' }">
                <PrimaryButton>Verify Bank Account</PrimaryButton>
              </router-link>
            </div>
          </div>
        </AlertToast>
      </div>
      <div
        v-if="getCurrentAccount"
        class="rounded-lg bg-white px-5 py-5 shadow-sm mx-1 my-5 md:my-0"
      >
        <h5 class="text-brand-black text-2xl">Account</h5>

        <div class="flex flex-col md:flex-row md:justify-between py-5 md:pr-10">
          <div class="flex flex-row justify-between py-1 md:flex-col">
            <p class="text-brand-mid-gray py-1">Total Balance</p>
            <h5 class="font-semibold py-1">
              {{ toCurrency(getCurrentAccount.total_balance) }}
            </h5>
          </div>

          <div class="flex flex-row justify-between py-1 md:flex-col">
            <p class="text-brand-mid-gray py-1">Available Balance</p>
            <h5 class="font-semibold py-1">
              {{ toCurrency(getCurrentAccount.total_available_balance) }}
            </h5>
          </div>

          <div class="flex flex-row justify-between py-1 md:flex-col">
            <p class="text-brand-mid-gray py-1">Pending Balance</p>
            <h5 class="font-semibold py-1">
              {{ toCurrency(getCurrentAccount.total_pending_deposits) }}
            </h5>
          </div>

          <div class="flex flex-row justify-between py-1 md:flex-col">
            <p class="text-brand-mid-gray py-1">Last Withdrawal</p>
            <h5 class="font-semibold py-1">
              {{ getCurrentAccount.last_withdrawal_date || "N/A" }}
            </h5>
          </div>

          <div class="flex flex-row justify-between py-1 md:flex-col">
            <p class="text-brand-mid-gray py-1">Next Withdrawal</p>
            <h5 class="font-semibold py-1">
              {{ nextWithdrawalDate }}
            </h5>
          </div>
        </div>

        <p class="text-xs text-brand-mid-gray">
          *Withdrawals are initiated on the first of every month; Fees processed after the 25th may
          not be included and will be carried over to the next withdrawal.
        </p>

        <template v-if="getAccounts && getAccounts.length > 1">
          <Line />
          <div class="py-3">
            <h2 class="py-3">Accounts list</h2>

            <div class="hidden md:flex flex-row">
              <div class="w-1/4">
                <h5>Agent</h5>
              </div>
              <div class="w-1/4">
                <h5>Company group</h5>
              </div>
              <div class="w-1/4">
                <h5>Available</h5>
              </div>
              <div class="w-1/4">
                <h5>Pending</h5>
              </div>
            </div>
          </div>
        </template>
      </div>

      <template v-if="getAccounts && getAccounts.length > 1">
        <div v-for="account in getAccounts" :key="account.id">
          <div class="hidden md:flex flex-row my-2 rounded-lg bg-white px-5 py-5 shadow-sm mx-1">
            <div class="w-1/4">
              <p>{{ account.landlord.name }}</p>
              <p class="text-brand-mid-gray text-xs">
                {{ account.landlord.username }}
              </p>
            </div>
            <div class="w-1/4">
              <p>{{ account.landlord.company }}</p>
            </div>
            <div class="w-1/4">
              <p>{{ toCurrency(account.available_balance) }}</p>
            </div>
            <div class="w-1/4">
              <p>{{ toCurrency(account.pending_deposits) }}</p>
            </div>
          </div>

          <div class="flex md:hidden flex-col my-2 rounded-sm bg-white px-5 py-5 shadow-sm mx-1">
            <div class="flex flex-col py-1">
              <!-- TODO: Add application url -->
              <p class="py-0.5">{{ account.landlord.name }}</p>
              <p class="text-brand-mid-gray text-xs py-0.5 italic">
                {{ account.landlord.username }}
              </p>

              <p class="text-lg">
                {{ account.landlord.company }}
              </p>
            </div>

            <div class="flex flex-row py-1 justify-between">
              <div class="flex flex-col">
                <p class="text-brand-mid-gray text-xs py-1">Available</p>
                <p class="">{{ toCurrency(account.available_balance) }}</p>
              </div>

              <div class="flex flex-col">
                <p class="text-brand-mid-gray text-xs py-1">Amount</p>
                <p class="">{{ toCurrency(account.pending_balance) }}</p>
              </div>
            </div>
          </div>
        </div>
        <p class="py-1 text-xs text-brand-mid-gray">
          *Only accounts with available balance greater than $0 are shown here.
        </p>
        <div class="flex justify-end">
          <Pagination
            :current="getAccountCurrentPage"
            :total="getAccountTotalCount"
            :per-page="25"
            @page-changed="loadAccounts"
          />
        </div>
      </template>

      <div class="rounded-lg bg-white px-5 py-5 shadow-sm mx-1 my-3 md:my-5">
        <h5 class="text-brand-black text-2xl py-2">Transaction list</h5>

        <div class="flex flex-row py-2">
          <div class="w-full md:w-1/2">
            <SearchInput @search="handleSearch" />
          </div>
          <div class="hidden md:flex items-center ml-10">
            <PrimaryButton
              @click="exportCSV"
              :disabled="isAccountLoading"
              :is-loading="isAccountLoading"
            >
              Export to Excel
            </PrimaryButton>
          </div>
        </div>

        <div class="hidden md:flex flex-col w-full">
          <Line />
        </div>

        <div class="hidden md:flex flex-row py-1">
          <div class="w-1/4">
            <h5>Applicant / Agent</h5>
          </div>
          <div class="w-1/4">
            <h5>Date</h5>
          </div>
          <div class="w-1/4">
            <h5>Type</h5>
          </div>
          <div class="w-1/4">
            <h5>Amount</h5>
          </div>
        </div>
      </div>

      <template v-if="isTransactionLoading">
        <Loading />
      </template>
      <template v-else>
        <template v-if="!getTransactions || !getTransactions.length">
          <template v-if="searchTerm">
            <div class="flex flex-col pb-3">
              <p class="py-1 px-2 text-sm md:text-base">
                No search results matching "{{ searchTerm }}", please try a different search.
              </p>
              <div class="flex items-center px-2">
                <div
                  class="md:hidden mt-1 py-0.5 px-1 flex items-center bg-white border border-brand-mid-gray rounded"
                  @click="clearSearch"
                >
                  <p class="text-brand-dark-gray text-sm">clear</p>
                </div>
              </div>
            </div>
          </template>
        </template>

        <template v-else>
          <div v-for="transaction in getTransactions" :key="transaction.id">
            <!-- START DESKTOP TRANSACTION ROW -->
            <div class="hidden md:flex flex-row my-2 rounded-lg bg-white px-5 py-5 shadow-sm mx-1">
              <div class="w-1/4">
                <!-- TODO: Add application url -->
                <template v-if="transaction.transaction_type === 'Withdrawal'">
                  <p>{{ transaction.landlord_name }}</p>
                </template>
                <template v-else>
                  <p>{{ transaction.applicant_name }}</p>
                  <p class="text-brand-mid-gray text-xs">
                    {{ transaction.landlord_name }}
                  </p>
                </template>
              </div>
              <div class="w-1/4">
                <p>{{ formatDate(transaction.created) }}</p>
              </div>
              <div class="w-1/4">
                <p>{{ transaction.transaction_type }}</p>
              </div>
              <div class="w-1/4">
                <div class="flex flex-row">
                  <div class="flex item-center">
                    <p :class="[transaction.net < 0 ? 'text-brand-red' : 'text-brand-black']">
                      {{ toCurrency(transaction.net) }}
                    </p>
                  </div>
                  <div
                    :class="[
                      'rounded-lg inline-block py-1 px-1 ml-2 h-full',
                      transaction.status === 'failure' ||
                      (transaction.status === 'deposit' && transaction.net < 0)
                        ? 'bg-brand-background-light-red'
                        : ['success', 'deposit'].includes(transaction.status)
                        ? 'bg-brand-background-light-blue'
                        : 'bg-brand-light-gray'
                    ]"
                  >
                    <div class="flex items-center">
                      <p
                        :class="[
                          'capitalize inline text-xs',
                          transaction.status === 'failure' ||
                          (transaction.status === 'deposit' && transaction.net < 0)
                            ? 'text-brand-red'
                            : ['success', 'deposit'].includes(transaction.status)
                            ? 'text-brand-blue'
                            : 'text-brand-dark-gray'
                        ]"
                      >
                        {{
                          transaction.status === "deposit" && transaction.net < 0
                            ? "Fee"
                            : transaction.status
                        }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <!-- END DESKTOP TRANSACTION ROW -->

            <!-- START MOBILE TRANSACTION ROW -->
            <div class="flex md:hidden flex-col my-2 rounded-sm bg-white px-5 py-5 shadow-sm mx-1">
              <div class="flex flex-col py-1">
                <!-- TODO: Add application url -->
                <template v-if="transaction.transaction_type === 'Withdrawal'">
                  <p class="py-0.5">{{ transaction.landlord_name }}</p>
                </template>
                <template v-else>
                  <p class="py-0.5">{{ transaction.applicant_name }}</p>
                  <p class="text-brand-mid-gray text-xs py-0.5">
                    {{ transaction.landlord_name }}
                  </p>
                </template>
                <p class="text-brand-mid-gray italic text-xs">
                  {{ formatDate(transaction.created) }}
                </p>
              </div>

              <div class="flex flex-row py-1 justify-between">
                <div class="flex flex-col">
                  <p class="text-brand-mid-gray text-xs py-1">Type</p>
                  <p class="">{{ transaction.transaction_type }}</p>
                </div>

                <div class="flex flex-col">
                  <p class="text-brand-mid-gray text-xs py-1">Amount</p>
                  <div class="flex flex-row">
                    <p :class="[transaction.net < 0 ? 'text-brand-red' : 'text-brand-black']">
                      {{ toCurrency(transaction.net) }}
                    </p>
                    <div
                      :class="[
                        'rounded-lg inline-block py-1 px-1 ml-2',
                        transaction.status === 'failure' ||
                        (transaction.status === 'deposit' && transaction.net < 0)
                          ? 'bg-brand-background-light-red'
                          : ['success', 'deposit'].includes(transaction.status)
                          ? 'bg-brand-background-light-blue'
                          : 'bg-brand-light-gray'
                      ]"
                    >
                      <div class="flex items-center">
                        <p
                          :class="[
                            'capitalize text-2xs inline',
                            transaction.status === 'failure' ||
                            (transaction.status === 'deposit' && transaction.net < 0)
                              ? 'text-brand-red'
                              : ['success', 'deposit'].includes(transaction.status)
                              ? 'text-brand-blue'
                              : 'text-brand-dark-gray'
                          ]"
                        >
                          {{
                            transaction.status === "deposit" && transaction.net < 0
                              ? "Fee"
                              : transaction.status
                          }}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <!-- END MOBILE TRANSACTION ROW -->
          </div>
        </template>
      </template>

      <div class="flex justify-end" v-if="getTransactions && getTransactions.length">
        <Pagination
          :current="getTransactionCurrentPage"
          :total="getTransactionTotalCount"
          :per-page="25"
          @page-changed="loadTransactions"
        />
      </div>

      <div class="absolute flex w-full items-center justify-center bottom-0 mx-1">
        <router-link
          class="text-brand-dark-gray py-3 hover:underline hover:text-brand-black"
          :to="{ name: 'home_my_payments_terms' }"
          >My Payments Terms of Use*</router-link
        >
      </div>
    </div>
  </template>
  <ActionToast ref="accountToast" />
</template>
<script>
import moment from "moment"
import { mapGetters } from "vuex"

import {
  ACCOUNT_GET,
  ACCOUNT_GET_LIST,
  TRANSACTION_GET_LIST,
  ACCOUNT_EXPORT_CSV
} from "@/store/types"

import { ExclamationIcon } from "@heroicons/vue/outline"

export default {
  components: {
    ExclamationIcon
  },
  computed: {
    ...mapGetters([
      "getCurrentAccount",
      "getAccounts",
      "isAccountLoading",
      "getTransactions",
      "isTransactionLoading",
      "getTransactionCurrentPage",
      "getTransactionTotalCount",
      "getAccountTotalCount",
      "getAccountCurrentPage"
    ]),
    nextWithdrawalDate() {
      const today = new Date()
      const nextDate = new Date(today.getFullYear(), today.getMonth() + 1, 1)
      return moment(nextDate).format("MM/DD/YYYY")
    }
  },
  async mounted() {
    await this.$store.dispatch(ACCOUNT_GET)
    this.showBankVerifyAlert = this.getCurrentAccount && !this.getCurrentAccount.verified
    this.loadAccounts(1)
    this.loadTransactions(1)
  },
  data() {
    return {
      searchTerm: "",
      showBankVerifyAlert: false
    }
  },
  methods: {
    closeBankVerifyAlert() {
      this.showBankVerifyAlert = false
    },
    async handleSearch(val) {
      this.searchTerm = val
      try {
        await this.$store.dispatch(TRANSACTION_GET_LIST, {
          page: 1,
          search: val
        })
      } catch (err) {
        console.log(err)
      }
    },
    formatDate(timestamp) {
      return moment(timestamp).format("MM/DD/YYYY")
    },
    async loadAccounts(page) {
      await this.$store.dispatch(ACCOUNT_GET_LIST, { page })
    },
    async loadTransactions(page) {
      await this.$store.dispatch(TRANSACTION_GET_LIST, {
        page,
        search: this.searchTerm
      })
    },
    clearSearch() {
      this.searchTerm = ""
      this.loadTransactions(1)
    },
    async exportCSV() {
      await this.$store.dispatch(ACCOUNT_EXPORT_CSV)
      this.$refs.accountToast.show({
        type: "success",
        hasClose: true,
        content: "We are generating your csv, we email you when it's ready.",
        icon: "check"
      })
    },
    toCurrency(amount) {
      if (amount) {
        return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount)
      }
      return "$0.00"
    }
  }
}
</script>
